import { ArrowRight, WalletCards } from "lucide-react";
import { Link } from "react-router-dom";

export function selectAppointmentOrthoCase(rows, caseId) {
  if (!caseId || !Array.isArray(rows)) return null;
  return rows.find((row) => row?.case?.id === caseId) || null;
}

export function buildOrthoCaseGlance(row) {
  if (!row?.case) return null;

  const payments = Array.isArray(row.payments) ? row.payments : [];
  const latestPayment = [...payments]
    .filter((payment) => payment?.occurred_at || payment?.created_at || payment?.event_date)
    .sort((a, b) => paymentTime(b) - paymentTime(a))[0] || null;
  const totalCollectedCentavos = Number(row?.balance?.total_collected_centavos || 0);
  const historicalPaidCentavos = (row.historical_payments || []).reduce(
    (sum, payment) => sum + Number(payment?.total_paid_prior_centavos || 0),
    0,
  );
  const isImported = row.case.migration_source === "migrated";
  const contractRemainingCentavos = contractRemaining(
    row,
    totalCollectedCentavos,
    isImported ? historicalPaidCentavos : 0,
  );
  const actualArCentavos = Number(
    row?.balance?.ar_outstanding_centavos
      ?? row?.balance?.balance_remaining_centavos
      ?? 0
  );

  return {
    caseId: row.case.id,
    systemName: row.case.system_name || row.case.system || "Ortho case",
    isImported,
    totalCollectedCentavos,
    contractRemainingCentavos,
    actualArCentavos,
    historicalPaidCentavos,
    lastPaymentCentavos: latestPayment
      ? Number(latestPayment.allocated_centavos ?? latestPayment.amount_centavos ?? 0)
      : null,
    lastPaymentAt: latestPayment
      ? latestPayment.occurred_at || latestPayment.created_at || latestPayment.event_date
      : null,
    lastPaymentMethod: latestPayment?.payment_method || null,
  };
}

export default function OrthoCaseGlance({
  row,
  patientId,
  loading = false,
  error = null,
}) {
  const summary = buildOrthoCaseGlance(row);

  if (loading || (!summary && !error)) {
    return (
      <section
        aria-label="Loading Ortho case summary"
        className="mt-4 border-t border-slate-100 pt-4"
        data-testid="checkout-ortho-glance-loading"
      >
        <div className="h-4 w-36 animate-pulse rounded bg-slate-100" />
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="space-y-2">
              <div className="h-3 w-20 animate-pulse rounded bg-slate-100" />
              <div className="h-5 w-28 animate-pulse rounded bg-slate-100" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error || !summary) {
    return (
      <section
        className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-4 text-sm"
        data-testid="checkout-ortho-glance-error"
      >
        <span className="font-semibold text-slate-700">Ortho case</span>
        <span className="text-slate-500">Summary unavailable.</span>
        {patientId ? <OrthoRecordLink patientId={patientId} /> : null}
      </section>
    );
  }

  const metricCount = summary.actualArCentavos > 0 ? 4 : 3;
  const hasRecordedPayment = summary.lastPaymentCentavos !== null;
  const lastPaymentText = hasRecordedPayment
    ? formatPeso(summary.lastPaymentCentavos)
    : summary.isImported
      ? "History incomplete"
      : "None recorded";

  return (
    <section
      aria-label="Ortho case at a glance"
      className="mt-4 border-t border-slate-100 pt-4"
      data-testid="checkout-ortho-glance"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-teal-50 text-teal-700">
            <WalletCards className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
          Ortho case
        </span>
        <span className="text-xs text-slate-500">{summary.systemName}</span>
        {patientId ? (
          <span className="ml-1">
            <OrthoRecordLink patientId={patientId} />
          </span>
        ) : null}
      </div>

      <dl
        className={`mt-3 grid grid-cols-2 gap-x-5 gap-y-4 ${
          metricCount === 4 ? "lg:grid-cols-4" : "sm:grid-cols-3"
        }`}
      >
        <Metric label="Last payment" value={lastPaymentText}>
          {hasRecordedPayment
            ? [formatDate(summary.lastPaymentAt), paymentMethodLabel(summary.lastPaymentMethod)].filter(Boolean).join(" · ")
            : summary.isImported
              ? "Last payment date was not imported"
              : "No confirmed case payment yet"}
        </Metric>
        <Metric
          label={summary.isImported ? "Collected in Dental OS" : "Total collected"}
          value={formatPeso(summary.totalCollectedCentavos)}
        >
          {summary.historicalPaidCentavos > 0
            ? `${formatPeso(summary.historicalPaidCentavos)} prior paid was imported`
            : "Confirmed payments for this case"}
        </Metric>
        <Metric label="Remaining balance" value={formatPeso(summary.contractRemainingCentavos)}>
          Future plan amount, not due today
        </Metric>
        {summary.actualArCentavos > 0 ? (
          <Metric
            label="Actual unpaid"
            value={formatPeso(summary.actualArCentavos)}
            danger
          >
            Completed work still unpaid
          </Metric>
        ) : null}
      </dl>
    </section>
  );
}

function Metric({ label, value, danger = false, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">
        {label}
      </dt>
      <dd
        className={`mt-0.5 text-base font-semibold tabular-nums ${
          danger ? "text-rose-700" : "text-slate-900"
        }`}
      >
        {value}
      </dd>
      <dd className={`mt-0.5 text-[11px] ${danger ? "text-rose-600" : "text-slate-500"}`}>
        {children}
      </dd>
    </div>
  );
}

function OrthoRecordLink({ patientId }) {
  return (
    <Link
      to={`/patients/${patientId}?tab=ortho`}
      className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
    >
      View payment history
      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
    </Link>
  );
}

function contractRemaining(row, totalCollectedCentavos, historicalPaidCentavos) {
  const packagePriceCentavos = Number(row?.billing?.package_price_centavos || 0);
  const writeoffCentavos = Number(row?.balance?.writeoff_centavos || 0);

  if (historicalPaidCentavos > 0 && packagePriceCentavos > 0) {
    return Math.max(
      packagePriceCentavos - totalCollectedCentavos - historicalPaidCentavos - writeoffCentavos,
      0,
    );
  }
  if (
    row?.balance?.contract_remaining_centavos !== undefined
    && row?.balance?.contract_remaining_centavos !== null
  ) {
    return Number(row.balance.contract_remaining_centavos || 0);
  }
  return Math.max(packagePriceCentavos - totalCollectedCentavos - writeoffCentavos, 0);
}

function paymentTime(payment) {
  const value = payment?.occurred_at || payment?.created_at || payment?.event_date;
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatPeso(centavos) {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(centavos || 0) / 100);
}

function formatDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function paymentMethodLabel(method) {
  if (!method) return "";
  if (String(method).toLowerCase() === "gcash") return "GCash";
  return String(method)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
